import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Navbar.css';

const NAV_LINKS = [
  { label: 'Accueil', href: '#hero' },
  { label: 'Services', href: '#services' },
  { label: 'Destinations', href: '#destinations' },
  { label: 'Nos Atouts', href: '#atouts' },
  { label: 'Nous Contacter', href: '#nous-contacter' },
  { label: 'Mode de Paiement', href: '#paiement' },
  { label: 'Suivi', href: '#tracking-service' },
];

export default function Navbar({ trackEvent }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef(null);

  // Défilement vers une section en tenant compte de la hauteur de la navbar fixe.
  // Si le menu mobile est ouvert, on attend la fin de sa fermeture (0,35 s) :
  // sinon l'animation de fermeture annule le défilement du navigateur.
  const scrollToSection = (href) => {
    const id = href.replace('#', '');
    const wasOpen = menuOpen;
    setMenuOpen(false);

    const doScroll = () => {
      const el = document.getElementById(id);
      if (!el) return;
      const navHeight = navRef.current ? navRef.current.offsetHeight : 0;
      const top = id === 'hero'
        ? 0
        : el.getBoundingClientRect().top + window.scrollY - navHeight;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      if (window.location.hash !== href) {
        window.history.pushState(null, '', href);
      }
    };

    if (wasOpen) setTimeout(doScroll, 380);
    else doScroll();
  };

  // Détection du scroll pour activer le glassmorphism renforcé
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleCTA = () => {
    // Tracking : clic sur le bouton CTA de la navbar
    trackEvent('navbar_cta_click', { cta_label: 'Demander un devis', location: 'navbar' });
    scrollToSection('#contact');
  };

  const handleNavClick = (e, label, href) => {
    e.preventDefault();
    trackEvent('nav_link_click', { link_label: label });
    scrollToSection(href);
  };

  return (
    <motion.nav
      ref={navRef}
      className={`navbar ${scrolled ? 'scrolled' : ''}`}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="navbar-inner">
        {/* Logo */}
        <a href="#hero" className="navbar-logo" onClick={(e) => handleNavClick(e, 'Logo', '#hero')}>
          <img src={require('../../assets/image1.png')} alt="Logo" className='monlogo'/>
          <span className="logo-text">
            <span className="logo-brand">Air transport</span>
            <span className="logo-tagline">Animalier</span>
          </span>
        </a>

        {/* Liens desktop */}
        <ul className="navbar-links">
          {NAV_LINKS.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                className="navbar-link"
                onClick={(e) => handleNavClick(e, link.label, link.href)}
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        {/* CTA + Hamburger */}
        <div className="navbar-actions">
          <button className="btn-primary navbar-cta" onClick={handleCTA}>
            Demander un devis
          </button>
          <button
            className={`hamburger ${menuOpen ? 'open' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </div>

      {/* Menu mobile */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
          >
            <ul>
              {NAV_LINKS.map((link) => (
                <li key={link.href}>
                  <a href={link.href} onClick={(e) => handleNavClick(e, link.label, link.href)}>{link.label}</a>
                </li>
              ))}
              <li>
                <button className="btn-primary mobile-cta" onClick={handleCTA}>
                  Demander un devis
                </button>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}